import { useQuery, type UseQueryResult, useMutation } from "@tanstack/react-query";
import { useState, useEffect } from "react";

import type * as auth from "@skilitics/auth-client";
import getAuthClient from "@skilitics/designer-auth";

export const useWorkspaceListQuery = (): UseQueryResult<readonly auth.WorkspaceInfo[]> => {
  return useQuery({
    queryKey: ["workspace", "list"],
    queryFn: async () => {
      // This is currently the only use of /self
      const tokens = await getAuthClient().fetchSelf();
      return tokens.workspaces;
    },
  });
};

const empty: readonly never[] = [];

export const useWorkspaceList = (): readonly auth.WorkspaceInfo[] => {
  return useWorkspaceListQuery().data ?? empty;
};

export const useInvalidateWorkspaceList = (): (() => Promise<readonly auth.WorkspaceInfo[]>) => {
  const { mutateAsync } = useMutation({
    mutationFn: async (_, { client }) => {
      // There's a bit of server-side caching going on at the moment.
      const updated = await getAuthClient().fetchSelf({ invalidate: true });
      await client.setQueryData(["workspace", "list"], updated.workspaces);
      return updated.workspaces;
    },
  });
  return mutateAsync;
};

export const useWorkspaceId = (): string | null => {
  const [state, setState] = useState<string | null>(null);

  useEffect(() => {
    const client = getAuthClient();
    return client.watch(() => {
      setState(client.apiToken ? client.apiToken.workspace : null);
    });
  }, []);

  return state;
};

export const useWorkspaceInfo = (): auth.WorkspaceInfo | null => {
  const workspaceId = useWorkspaceId();
  const workspaces = useWorkspaceList();
  return workspaceId ? (workspaces.find((w) => w.id === workspaceId) ?? null) : null;
};
